import React from 'react';
import { HashtagDisplay } from '../shared';

const MapHoverPopup = ({
  hoveredResult,
  hoverPosition,
  containerWidth,
  isPopupMaximized,
  getMarkerColor,
  categoryColorMap,
  onMouseEnter,
  onMouseLeave,
  onToggleMaximize,
  onClose,
  onMarkerClick
}) => {
  if (!hoveredResult) return null;

  const markerColor = getMarkerColor(hoveredResult.article_type);

  return (
    <div
      data-testid="map-popup"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'absolute',
        left: Math.min(hoverPosition.x + 15, containerWidth - (isPopupMaximized ? 420 : 320)),
        top: Math.max(hoverPosition.y - (isPopupMaximized ? 200 : 150), 10),
        width: isPopupMaximized ? 400 : 300,
        maxHeight: isPopupMaximized ? 380 : 280,
        background: 'linear-gradient(145deg, rgba(253, 251, 247, 0.98), rgba(25, 15, 50, 0.98))',
        borderRadius: 12,
        border: `2px solid ${markerColor}`,
        boxShadow: `0 8px 32px rgba(0,0,0,0.6), 0 0 20px ${markerColor}40`,
        zIndex: 1000,
        pointerEvents: 'auto',
        overflow: 'hidden',
        animation: 'popupFadeIn 0.15s ease-out',
        backdropFilter: 'blur(10px)'
      }}
    >
      {/* Header with Close and Maximize buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 12px',
        background: `linear-gradient(90deg, ${markerColor}30, transparent)`,
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        {/* Article type and location */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <span style={{
            background: markerColor,
            padding: '3px 8px', borderRadius: 10,
            fontSize: '0.65rem', fontWeight: 600, color: '#0F172A'
          }}>
            {hoveredResult.article_type}
          </span>
          {hoveredResult.extractedPlace && (
            <span style={{ color: '#10b981', fontSize: '0.7rem' }}>
              📍 {hoveredResult.extractedPlace.charAt(0).toUpperCase() + hoveredResult.extractedPlace.slice(1)}
            </span>
          )}
        </div>

        {/* Control buttons */}
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleMaximize(); }}
            data-testid="popup-maximize-btn"
            title={isPopupMaximized ? 'Minimize' : 'Maximize'}
            style={{
              width: 24, height: 24,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.1)',
              border: 'none', borderRadius: 4,
              color: '#a1a1aa', cursor: 'pointer',
              fontSize: '0.85rem', transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(252, 211, 77, 0.3)';
              e.currentTarget.style.color = '#047857';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.color = '#a1a1aa';
            }}
          >
            {isPopupMaximized ? '⊟' : '⊞'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            data-testid="popup-close-btn"
            title="Close"
            style={{
              width: 24, height: 24,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(239, 68, 68, 0.2)',
              border: 'none', borderRadius: 4,
              color: '#ef4444', cursor: 'pointer',
              fontSize: '1rem', fontWeight: 'bold', transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.4)';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
              e.currentTarget.style.color = '#ef4444';
            }}
          >
            ×
          </button>
        </div>
      </div>

      {/* Content area */}
      <div style={{
        padding: 12,
        overflowY: 'auto',
        maxHeight: isPopupMaximized ? 300 : 180
      }}>
        {/* Category badges */}
        {hoveredResult.categories && hoveredResult.categories.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
            {hoveredResult.categories.slice(0, isPopupMaximized ? 8 : 3).map((cat, idx) => (
              <span key={idx} style={{
                background: categoryColorMap[cat] || 'rgba(252, 211, 77, 0.3)',
                padding: '2px 6px', borderRadius: 8,
                fontSize: '0.6rem', fontWeight: 500, color: '#0F172A'
              }}>
                {cat}
              </span>
            ))}
            {!isPopupMaximized && hoveredResult.categories.length > 3 && (
              <span style={{ color: '#71717a', fontSize: '0.6rem' }}>
                +{hoveredResult.categories.length - 3}
              </span>
            )}
          </div>
        )}

        <h4 style={{
          color: '#0F172A',
          margin: '0 0 8px 0',
          fontSize: isPopupMaximized ? '0.95rem' : '0.85rem',
          lineHeight: 1.3
        }}>
          {isPopupMaximized
            ? hoveredResult.title || 'Untitled'
            : (hoveredResult.title?.substring(0, 70) || 'Untitled') + (hoveredResult.title?.length > 70 ? '...' : '')}
        </h4>
        <p style={{
          color: '#a1a1aa',
          fontSize: '0.75rem',
          margin: '0 0 10px 0',
          lineHeight: 1.5
        }}>
          {isPopupMaximized
            ? hoveredResult.snippet || 'No description available'
            : (hoveredResult.snippet?.substring(0, 120) || 'No description') + (hoveredResult.snippet?.length > 120 ? '...' : '')}
        </p>

        {/* Hashtags */}
        {hoveredResult.hashtags && (
          <HashtagDisplay hashtags={hoveredResult.hashtags} small={true} />
        )}

        {/* Quality + Doc Type Tags Row */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
          {hoveredResult.content_quality_score != null && (
            <span style={{
              padding: '2px 6px', borderRadius: 6, fontSize: '0.6rem', fontWeight: 600,
              background: hoveredResult.content_quality_score >= 80 ? 'rgba(16,185,129,0.2)' :
                          hoveredResult.content_quality_score >= 60 ? 'rgba(245, 158, 11,0.2)' :
                          hoveredResult.content_quality_score >= 30 ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)',
              color: hoveredResult.content_quality_score >= 80 ? '#10b981' :
                     hoveredResult.content_quality_score >= 60 ? '#3b82f6' :
                     hoveredResult.content_quality_score >= 30 ? '#f59e0b' : '#ef4444',
              border: `1px solid ${hoveredResult.content_quality_score >= 80 ? 'rgba(16,185,129,0.3)' :
                     hoveredResult.content_quality_score >= 60 ? 'rgba(245, 158, 11,0.3)' :
                     hoveredResult.content_quality_score >= 30 ? 'rgba(245,158,11,0.3)' : 'rgba(239,68,68,0.3)'}`
            }}>
              Quality: {hoveredResult.content_quality_score}
            </span>
          )}
          {hoveredResult.location_source && (
            <span style={{
              padding: '2px 6px', borderRadius: 6, fontSize: '0.6rem',
              background: 'rgba(252, 211, 77,0.15)', color: '#047857',
              border: '1px solid rgba(252, 211, 77,0.25)'
            }}>
              {hoveredResult.location_source}
            </span>
          )}
        </div>

        {/* Clickable URL */}
        {hoveredResult.url && (
          <a
            href={hoveredResult.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="popup-url-link"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'block', color: '#FCD34D', fontSize: '0.7rem',
              textDecoration: 'none', marginBottom: 8, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap'
            }}
          >
            <i className="fas fa-external-link-alt" style={{ marginRight: 4, fontSize: '0.6rem' }} />
            {hoveredResult.url.substring(0, 60)}{hoveredResult.url.length > 60 ? '...' : ''}
          </a>
        )}

        {/* Click instruction */}
        <div
          onClick={() => onMarkerClick(hoveredResult)}
          style={{
            marginTop: 10, padding: '8px 12px',
            background: `${markerColor}25`,
            borderRadius: 8, textAlign: 'center',
            cursor: 'pointer', transition: 'all 0.2s',
            border: `1px solid ${markerColor}40`
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = `${markerColor}40`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = `${markerColor}25`;
          }}
        >
          <span style={{ color: '#0F172A', fontSize: '0.75rem', fontWeight: 600 }}>
            🔗 {isPopupMaximized ? 'Open Article in New Tab' : 'Click to Open'}
          </span>
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes popupFadeIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default MapHoverPopup;
